"use client"

import { Images } from "@/constants/images"
import { useTextExpansion } from "@/hook/useTextExpansion"
import { toImageSrc } from "@/lib/utils"
import { TimelineItem as TimelineItemType } from "@/types/types"
import { t } from "i18next"
import Image from "next/image"
import { twMerge } from "tailwind-merge"

interface TimelineItemProps {
  item: TimelineItemType
  className?: string
  showImage?: boolean
}

// Tách phần content ra thành component riêng để tái sử dụng
const TimelineContent = ({ item, imageUrl }: { item: TimelineItemType; imageUrl?: string | null }) => {
  const { isExpanded, setIsExpanded, showSeeMore, visibleText, hiddenText, descriptionRef, measureRef } =
    useTextExpansion(item.description, 8)

  return (
    <div className="flex flex-col gap-4 sm:gap-6 flex-1">
      {/* Content wrapper with background and padding */}
      <div className="bg-[#F1F6FA] sm:bg-transparent p-6 sm:pt-6 sm:pb-0 sm:px-0 flex flex-col gap-[0.9375rem] sm:gap-4 rounded-lg sm:rounded-none">
        <div className="flex flex-col gap-[0.9375rem]">
          <h3 className="text-2xl leading-[1.625rem] font-black text-[#212529] sm:uppercase">{item.title}</h3>
          <div className="flex flex-col gap-1">
            <span className="text-xl font-bold leading-[1.375rem] text-[#212529]">
              {item.date} - {item.subtitle}
            </span>
          </div>
        </div>

        <div className="text-base text-[#333] leading-[1.5rem] font-normal flex-1 relative">
          {/* Mobile: Always show full description */}
          <p className="sm:hidden text-base text-[#333] leading-[1.5rem] font-normal">{item.description}</p>

          {/* Desktop: Show with "See more" functionality */}
          <div className="hidden sm:block">
            {/* Element ẩn để đo chiều cao - phải có cùng styling với element hiển thị */}
            <p
              ref={measureRef}
              className="absolute invisible text-base sm:text-lg whitespace-normal"
              style={{
                width: "100%",
                top: 0,
                left: 0,
                zIndex: -1,
                pointerEvents: "none",
              }}
            >
              {item.description}
            </p>

            {/* Element hiển thị - "... See more" inline trong dòng cuối cùng */}
            <p ref={descriptionRef} className="text-base text-[#333] leading-[1.5rem] font-normal">
              {isExpanded ? (
                <>{item.description}</>
              ) : (
                <>
                  {visibleText}
                  {showSeeMore && (
                    <span className="inline whitespace-nowrap">
                      <span>...</span>
                      <button
                        onClick={() => setIsExpanded(true)}
                        type="button"
                        className="text-[#18AF4E] hover:text-green-700 font-medium transition-colors cursor-pointer ml-1 bg-transparent border-none p-0 inline"
                      >
                        {t("See more")}
                      </button>
                    </span>
                  )}
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Mobile: Image below content wrapper */}
      {imageUrl && (
        <div className="sm:hidden w-full">
          <div className="relative w-full h-[345px] rounded-lg overflow-hidden">
            <Image src={imageUrl} alt={item.title} fill className="object-cover" sizes="100vw" />
          </div>
        </div>
      )}

      {/* Wing Logo - Hard coded at bottom right of content - Hidden on mobile */}
      <div className="hidden sm:flex mt-auto justify-start relative w-[334px] h-[120px]">
        <Image src={Images.tamdaWing} alt="Tamda Wing" fill className="object-contain " />
      </div>
    </div>
  )
}

export const TimelineItem = ({ item, className, showImage = true }: TimelineItemProps) => {
  const imageUrl = item.image ? toImageSrc(item.image) : null

  // Nếu showImage và có imageUrl, hiển thị layout với image
  if (showImage && imageUrl) {
    return (
      <div className={twMerge("w-full", className)}>
        <div className="flex flex-col sm:flex-row gap-6 sm:gap-8">
          {/* Image - Left side for desktop, hidden on mobile (image shown in TimelineContent on mobile) */}
          <div className="hidden sm:block w-full sm:w-1/2 flex-shrink-0">
            <div className="relative w-full h-[300px] sm:h-[600px] rounded-lg overflow-hidden">
              <Image
                src={imageUrl}
                alt={item.title}
                fill
                className="object-cover"
                sizes="(max-width: 640px) 100vw, 50vw"
              />
            </div>
          </div>

          {/* Content - Right side for desktop, full width on mobile */}
          <div className="flex-1 relative">
            <TimelineContent item={item} imageUrl={imageUrl} />
          </div>
        </div>
      </div>
    )
  }

  // Text-only layout (no image hoặc showImage = false)
  return (
    <div className={twMerge("w-full h-full flex flex-col", className)}>
      <TimelineContent item={item} imageUrl={imageUrl} />
    </div>
  )
}
